import type { FeatureCollection, LineString, Position } from 'geojson';
import { type FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';

export interface Props {
  /** Where the track ends up when open. */
  tracks: Array<Array<Position>>;
  /**
   * Where it starts from, point for point the same length as `tracks`. The layer morphs from here
   * to `tracks` when it opens and back when it closes.
   */
  fromTracks?: Array<Array<Position>>;
  open?: boolean;
  /** Reports when the track appears (opening starts) and disappears (closing ends). */
  onShownChange?: (shown: boolean) => void;
  color: string;
  /** Outer width in px; the white inner corridor is this minus two borders. */
  width?: number;
  border?: number;
  /** Moves the dashes in the direction of the track. */
  animate?: boolean;
  id?: string;
}

// Mapbox's "animated line" sequence: dash, gap, dash in line widths. Each step shifts the pattern
// half a unit forward, so the dashes travel from the first coordinate towards the last.
const DASH_SCALE = 2;
const DASH_SEQUENCE = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
].map((step) => step.map((length) => length * DASH_SCALE));
const STEP_IN_MILLIS = 86;
// Opening runs in phases: 1. move as a thin line, 2. widen into the white corridor, 3. dashes.
// Closing runs them backwards.
const MOVE_IN_MILLIS = 600;
const WIDEN_IN_MILLIS = 300;
const MORPH_IN_MILLIS = MOVE_IN_MILLIS + WIDEN_IN_MILLIS;
const MOVE_SHARE = MOVE_IN_MILLIS / MORPH_IN_MILLIS;
const THIN_WIDTH = 2;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

const lerp = (from: Position, to: Position, t: number): Position => [
  from[0] + (to[0] - from[0]) * t,
  from[1] + (to[1] - from[1]) * t,
];

function lines(coordinateLists: Array<Array<Position>>): FeatureCollection<LineString> {
  return {
    type: 'FeatureCollection',
    features: coordinateLists.map((coordinates) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {},
    })),
  };
}

/** A road-like track: coloured border, white corridor, and dashes that flow along the track. */
export const FlowTrackLayer: FC<Props> = ({
  tracks,
  fromTracks,
  open = true,
  onShownChange,
  color,
  width = 10,
  border = 2,
  animate = true,
  id: settedId,
}) => {
  const genId = useId();
  const id = settedId || genId;
  const { current: map } = useMap();
  const morphs = fromTracks != null;
  const [progress, setProgress] = useState(open || !morphs ? 1 : 0);
  const progressRef = useRef(progress);

  useEffect(() => {
    const target = open || !morphs ? 1 : 0;
    const startProgress = progressRef.current;
    const update = (value: number) => {
      progressRef.current = value;
      setProgress(value);
    };
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || startProgress === target) {
      update(target);
      return;
    }
    // A morph reversed half-way takes only the time for the distance left.
    const duration = MORPH_IN_MILLIS * Math.abs(target - startProgress);
    let frame = 0;
    let start: number | null = null;
    const tick = (timestamp: number) => {
      start ??= timestamp;
      const share = Math.min((timestamp - start) / duration, 1);
      update(startProgress + (target - startProgress) * share);
      if (share < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [open, morphs]);

  const shown = progress > 0;
  useEffect(() => onShownChange?.(shown), [shown]);
  // Dashes only once the morph has finished, so the moving track is not distracting.
  const settled = progress === 1;
  const eased = easeInOut(Math.min(progress / MOVE_SHARE, 1));
  const widened = easeInOut(Math.max((progress - MOVE_SHARE) / (1 - MOVE_SHARE), 0));
  const current = useMemo(() => {
    if (!shown) return [];
    if (fromTracks == null || eased === 1) return tracks;
    return tracks.map((track, k) =>
      track.map((to, i) => lerp(fromTracks[k]?.[i] ?? to, to, eased)),
    );
  }, [tracks, fromTracks, eased, shown]);

  const trackData = useMemo(() => lines(current), [current]);

  const dashLayerId = `layer-flow-dash-${id}`;
  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reducedMotion || map == null || !settled) return;

    let frame = 0;
    let lastStep = -1;
    const tick = (timestamp: number) => {
      const step = Math.floor(timestamp / STEP_IN_MILLIS) % DASH_SEQUENCE.length;
      if (step !== lastStep && map.getLayer(dashLayerId) != null) {
        map.getMap().setPaintProperty(dashLayerId, 'line-dasharray', DASH_SEQUENCE[step]);
        lastStep = step;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, map, dashLayerId, settled]);

  const layout = { 'line-join': 'round', 'line-cap': 'round' } as const;
  return (
    <>
      <Source type="geojson" data={trackData} id={id} key={id}>
        <Layer
          id={`layer-flow-casing-${id}`}
          type="line"
          layout={layout}
          paint={{ 'line-color': color, 'line-width': THIN_WIDTH + (width - THIN_WIDTH) * widened }}
        />
        <Layer
          id={`layer-flow-inner-${id}`}
          type="line"
          layout={layout}
          paint={{ 'line-color': '#ffffff', 'line-width': (width - 2 * border) * widened }}
        />
        <Layer
          id={dashLayerId}
          type="line"
          layout={{ 'line-join': 'round', visibility: settled ? 'visible' : 'none' }}
          paint={{
            'line-color': color,
            'line-width': border,
            'line-dasharray': DASH_SEQUENCE[0],
          }}
        />
      </Source>
    </>
  );
};
